import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import React, { useEffect, useRef, useState } from "react";
import { truncate } from "lodash";

import { BarChart } from "@/Components/v2/molecule/BarChart/BarChart";
import { LineChart } from "@/Components/v2/molecule/LineChart/LineChart";
import { toCompactNumber } from "@/Utils/toCompactNumber";

import ConditionalScroll from "@/shared/components/ConditionalScroll";
import {
  ApiCalloutBlock,
  ApiChartBlock,
  ApiDividerBlock,
  ApiHeadingBlock,
  ApiInsightBlock,
  ApiListBlock,
  ApiMetricBlock,
  ApiRecommendationBlock,
  ApiStructuredContent,
  ApiTableBlock,
  ApiTextBlock,
} from "../../api/chats.api";
import { Markdown } from "./Markdown";

// Define the union type for content blocks
type ApiContentBlock =
  | ApiTextBlock
  | ApiHeadingBlock
  | ApiListBlock
  | ApiMetricBlock
  | ApiInsightBlock
  | ApiRecommendationBlock
  | ApiCalloutBlock
  | ApiDividerBlock;

type ApiGraphBlock = ApiChartBlock | ApiTableBlock | ApiDividerBlock;

interface StructuredContentRendererProps {
  structuredContent: ApiStructuredContent | null | undefined;
}

const variantMap: Record<string, "body1" | "subtitle1" | "caption"> = {
  body: "body1",
  subtitle: "subtitle1",
  caption: "caption",
};

const emphasisStyles: Record<string, React.CSSProperties> = {
  bold: { fontWeight: 700 },
  italic: { fontStyle: "italic" },
  underline: { textDecoration: "underline" },
};

export const StructuredContentRenderer: React.FC<StructuredContentRendererProps> = ({
  structuredContent,
}) => {
  const theme = useTheme();
  const { chatTheme } = theme;

  if (!structuredContent) {
    console.log("No structured content or analysis found");
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: chatTheme?.spacing?.componentMargin ?? 2,
      }}
    >
      <AnalysisRenderer structuredContent={structuredContent} />
    </Box>
  );
};

// Component for handling the analysis structure
interface AnalysisRendererProps {
  structuredContent: any | null | undefined;
}

const AnalysisRenderer: React.FC<AnalysisRendererProps> = ({ structuredContent }) => {
  const theme = useTheme();
  const { chatTheme } = theme;

  const hasConversationBlocks =
    structuredContent?.conversation && structuredContent.conversation.text.length > 0;
  const hasAnalysisBlocks =
    structuredContent?.analysis && structuredContent.analysis?.text?.length > 0;

  const hasAnalysisGraphs =
    structuredContent?.analysis && structuredContent.analysis?.graphs?.length > 0;

  const hasSummaryBlocks = structuredContent?.response && structuredContent.response.length > 0;
  const hasTextBlocks = structuredContent?.detailed && structuredContent.detailed.length > 0;
  const hasGraphBlocks = structuredContent?.graphs && structuredContent.graphs.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Text Content Section */}
      {hasConversationBlocks && (
        <AccordionDetails>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: chatTheme?.spacing?.componentMargin ?? 1.5,
            }}
          >
            <ProgressiveBlockRenderer blocks={structuredContent?.conversation?.text ?? []} />
          </Box>
        </AccordionDetails>
      )}
      {hasAnalysisBlocks && (
        <AccordionDetails>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: chatTheme?.spacing?.componentMargin ?? 1.5,
            }}
          >
            <ProgressiveBlockRenderer blocks={structuredContent?.analysis?.text ?? []} />
          </Box>
        </AccordionDetails>
      )}
      {hasAnalysisGraphs && (
        <AccordionDetails>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: chatTheme?.spacing?.componentMargin ?? 1.5,
            }}
          >
            <ProgressiveBlockRenderer blocks={structuredContent?.analysis?.graphs ?? []} />
          </Box>
        </AccordionDetails>
      )}
      {hasSummaryBlocks && (
        <AccordionDetails>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: chatTheme?.spacing?.componentMargin ?? 1.5,
            }}
          >
            <ProgressiveBlockRenderer blocks={structuredContent?.response ?? []} />
          </Box>
        </AccordionDetails>
      )}

      {/* Text Content Section */}
      {hasTextBlocks && (
        <Accordion
          disableGutters
          sx={{
            width: "100%",
            boxShadow: "none",
            "&:before": { opacity: "1 !important" }, // Keep top divider when expanded
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 1 }}>
            <Typography variant="h6">Detailed Performance Summary</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: chatTheme?.spacing?.componentMargin ?? 1.5,
              }}
            >
              <ProgressiveBlockRenderer blocks={structuredContent?.detailed ?? []} />
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Graphs Section */}
      {hasGraphBlocks && <ProgressiveBlockRenderer blocks={structuredContent?.graphs ?? []} />}
    </Box>
  );
};

// Component for progressive rendering of blocks
interface ProgressiveBlockRendererProps {
  blocks: (ApiContentBlock | ApiGraphBlock)[];
}

const ProgressiveBlockRenderer: React.FC<ProgressiveBlockRendererProps> = ({ blocks }) => {
  const [visibleCount, setVisibleCount] = useState<number>(0);
  const cancelRef = useRef<boolean>(false);

  useEffect(() => {
    cancelRef.current = false;
    setVisibleCount(0);

    if (!Array.isArray(blocks) || blocks.length === 0) return;

    const stepDelayMs = 40;

    const schedule = (cb: () => void) => {
      const ric = (window as any)?.requestIdleCallback as
        | ((cb: () => void, opts?: { timeout?: number }) => number)
        | undefined;
      if (ric) {
        ric(cb, { timeout: stepDelayMs });
      } else {
        setTimeout(cb, stepDelayMs);
      }
    };

    let index = 0;
    const revealNext = () => {
      if (cancelRef.current) return;
      index += 1;
      setVisibleCount((prev) => Math.min(prev + 1, blocks.length));
      if (index < blocks.length) {
        schedule(revealNext);
      }
    };

    schedule(revealNext);

    return () => {
      cancelRef.current = true;
    };
  }, [blocks]);

  return (
    <>
      {blocks.slice(0, visibleCount).map((block) => {
        const key = block.id ?? Math.random();
        return <ContentBlock key={key} block={block} />;
      })}
    </>
  );
};

interface ContentBlockProps {
  block: ApiContentBlock | ApiGraphBlock;
}

const ContentBlock: React.FC<ContentBlockProps> = ({ block }) => {
  const theme = useTheme();

  const chatTheme = (theme as any).chatTheme ?? {};

  switch (block.type) {
    case "text": {
      const text = block as ApiTextBlock;

      // Handle markdown variant with proper markdown rendering
      if (text.variant === "markdown") {
        return <Markdown>{text.content}</Markdown>;
      }

      // Handle regular text variants
      const variant = variantMap[text.variant ?? "body"];
      const emphasisStyle = emphasisStyles[text.emphasis ?? ""];
      return (
        <Typography component="div" variant={variant} sx={emphasisStyle}>
          <Markdown>{text.content}</Markdown>
        </Typography>
      );
    }

    case "heading": {
      const heading = block as ApiHeadingBlock;
      const headingVariant = `h${heading.level}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
      return (
        <Typography variant={headingVariant} sx={{ mt: 1, mb: 0.5 }}>
          {heading.content}
        </Typography>
      );
    }

    case "list": {
      const list = block as ApiListBlock;
      const Component = list.ordered ? "ol" : "ul";
      return (
        <List
          component={Component}
          sx={{
            listStyleType: list.ordered ? "decimal" : "disc",
            pl: 4, // Add padding for the markers
            "& .MuiListItem-root": {
              display: "list-item",
              listStyleType: "inherit",
              listStylePosition: "outside",
              py: 0.25,
            },
          }}
        >
          {Array.isArray(list.items) &&
            list.items.map((item, idx) => (
              <ListItem key={idx}>
                <Markdown>{item}</Markdown>
              </ListItem>
            ))}
        </List>
      );
    }

    case "chart": {
      const chart = block as ApiChartBlock;

      let chartElement: React.ReactNode = null;

      // Case 1: chart.data provided in our simplified format (labels + datasets)
      if (Array.isArray(chart.data?.labels) && chart.chartType === "bar") {
        const series = chart.data.datasets.map((ds) => ({
          name: ds.label,
          data: ds.data,
        }));
        chartElement = (
          <BarChart
            axisLabels={chart.data.labels}
            series={series}
            yAxisLabel={chart?.meta?.yAxis?.label ?? chart?.meta?.yAxis}
            tooltipOptions={{
              padding: [12, 16],
            }}
            overrides={{
              xAxis: {
                data: chart.data.labels,
                axisLabel: {
                  interval: 0,
                  color: theme.palette.brandedTypography?.textColor,
                  fontSize: 12,
                  rotate: 45,
                  padding: [12, 0, 0, 0],
                },
              },
              grid: {
                bottom: 80, // Add more bottom space for rotated labels
              },
            }}
          />
        );
      }
      // Case 1b: line chart with labels + datasets format
      else if (Array.isArray(chart.data?.labels) && chart.chartType === "line") {
        const series = chart.data.datasets.map((ds) => ({
          name: ds.label,
          type: "line" as const,
          data: ds.data,
          // color: ds.backgroundColor,
        }));

        // Simple dynamic scaling - get min/max and add padding
        const allValues = chart.data.datasets
          .flatMap((ds) => ds.data)
          .filter((v) => typeof v === "number") as number[];

        const minValue = Math.min(...allValues);
        const maxValue = Math.max(...allValues);
        const range = maxValue - minValue;

        // Add 10% padding to top and bottom
        const yAxisMax = maxValue + range * 0.1;
        const yAxisMin = Math.max(0, minValue - range * 0.1); // Don't go below 0 for positive data

        chartElement = (
          <LineChart
            axisLabels={chart.data.labels}
            series={series}
            yAxisLabel={chart?.meta?.yAxis?.label ?? chart?.meta?.yAxis}
            tooltipOptions={{
              padding: [12, 16],
            }}
            xAxisLabelFormatter={(value: string) => truncate(value, { length: 16 })}
            overrides={{
              xAxis: {
                data: chart.data.labels,
                axisLabel: {
                  interval: 0,
                  color: theme.palette.brandedTypography?.textColor,
                  fontSize: 14,
                  formatter: (value: string) => truncate(value, { length: 16 }),
                  padding: [12, 0, 0, 0],
                },
              },
              yAxis: {
                min: yAxisMin,
                max: yAxisMax,
                axisLabel: {
                  formatter: (value: number) => toCompactNumber(value, 1),
                },
              },
            }}
          />
        );
      }
      // Case 1c: pie chart with labels + datasets format
      else if (Array.isArray(chart.data?.labels) && chart.chartType === "pie") {
        const dataset = chart.data.datasets?.[0];

        if (!dataset) {
          chartElement = (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="error">
                  Invalid pie chart data: missing dataset
                </Typography>
              </CardContent>
            </Card>
          );
        } else {
          const pieData = chart.data.labels.map((label, index) => ({
            value: dataset.data[index],
            name: label,
            itemStyle: {
              color: Array.isArray(dataset.backgroundColor)
                ? dataset.backgroundColor[index]
                : dataset.backgroundColor,
            },
          }));

          const option = {
            tooltip: {
              trigger: "item",
              formatter: (params: any) => {
                const value = params.value;
                const percent = params.percent;
                return `${params.name}<br/>${value.toLocaleString()}<br/>${percent}%`;
              },
            },
            legend: {
              orient: "vertical",
              left: "left",
              textStyle: {
                color: theme.palette.brandedTypography?.textColor || theme.palette.text?.primary,
              },
            },
            series: [
              {
                type: "pie",
                radius: "50%",
                data: pieData,
                emphasis: {
                  itemStyle: {
                    shadowBlur: 10,
                    shadowOffsetX: 0,
                    shadowColor: "rgba(0, 0, 0, 0.5)",
                  },
                },
                label: {
                  formatter: (params: any) => {
                    return `${params.name}\n${params.percent}%`;
                  },
                },
              },
            ],
          };

          chartElement = <ReactECharts option={option} style={{ minHeight: "400px" }} />;
        }
      }
      // Case 2: chart.data is already an ECharts-like option (xAxis, series, etc.)
      else if (
        chart.data &&
        typeof chart.data === "object" &&
        "xAxis" in chart.data &&
        "series" in chart.data
      ) {
        const option = {
          ...chart.data,
          tooltip: { trigger: "axis" },
        };
        chartElement = <ReactECharts option={option} style={{ minHeight: "300px" }} />;
      } else {
        chartElement = (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2">
                {`[Chart of type ${chart.chartType} is not yet supported]`}
              </Typography>
            </CardContent>
          </Card>
        );
      }

      return (
        <Accordion
          disableGutters
          sx={{
            width: "100%",
            boxShadow: "none",
            "&:before": { opacity: "1 !important" }, // Keep top divider when expanded
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 1 }}>
            <Typography variant="h6">{chart.title ?? "Chart"}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ConditionalScroll
              unitWidthPx={120}
              scrollThreshold={9}
              numberOfItems={chart.data?.labels?.length ?? 0}
            >
              <Box
                sx={{
                  minWidth: (() => {
                    let labels: string[] = [];
                    if (Array.isArray(chart.data?.labels)) {
                      labels = chart.data.labels;
                    } else if (
                      chart.data &&
                      typeof chart.data === "object" &&
                      "xAxis" in chart.data
                    ) {
                      const xAxis = chart.data.xAxis as any;
                      if (Array.isArray(xAxis?.data)) {
                        labels = xAxis.data;
                      }
                    }
                    const numLabels = labels.length || 1;
                    return `${Math.max(numLabels * 60, 500)}px`;
                  })(),
                }}
              >
                {chartElement}
              </Box>
            </ConditionalScroll>
          </AccordionDetails>
        </Accordion>
      );
    }

    case "metric": {
      const metric = block as ApiMetricBlock;
      return (
        <Box sx={{ mt: chatTheme.spacing?.componentMargin ?? 1 }} py={1}>
          <Typography variant="subtitle2" color="textSecondary">
            {metric.title}
          </Typography>
          <Typography variant="h4">{metric.value}</Typography>
          {metric.subtitle && (
            <Typography variant="body2" color="textSecondary">
              {metric.subtitle}
            </Typography>
          )}
          {metric.change && (
            <Typography
              variant="body2"
              color={
                metric.change.direction === "up"
                  ? "success.main"
                  : metric.change.direction === "down"
                    ? "error.main"
                    : "textSecondary"
              }
            >
              {metric.change.direction === "up"
                ? "↑"
                : metric.change.direction === "down"
                  ? "↓"
                  : "→"}
              {metric.change.value}% {metric.change.period}
            </Typography>
          )}
        </Box>
      );
    }

    case "table": {
      const table = block as ApiTableBlock;
      return (
        <Box sx={{ mt: chatTheme.spacing?.componentMargin ?? 1, width: "100%", overflowX: "auto" }}>
          {table.title && (
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              <Markdown>{table.title}</Markdown>
            </Typography>
          )}
          <Box
            sx={{
              border: (theme) =>
                theme.palette.mode === "dark" ? "1px solid #555555" : "1px solid #e0e0e0",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <Table
              size="small"
              sx={{
                minWidth: "100%",
                fontSize: "14px",
                "& .MuiTableCell-root": {
                  borderBottom: (theme) =>
                    theme.palette.mode === "dark" ? "1px solid #555555" : "1px solid #e0e0e0",
                  padding: "12px 16px",
                },
                "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
                  borderBottom: "none",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {table.headers.map((header, idx) => (
                    <TableCell
                      key={idx}
                      sx={{
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "#353535" : "#EEF3FD",
                        wordBreak: "break-word",
                        "& *": {
                          fontWeight: "600 !important",
                        },
                      }}
                    >
                      <Markdown>{header}</Markdown>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {table.rows.map((row, idx) => (
                  <TableRow key={idx}>
                    {row.map((cell, cidx) => (
                      <TableCell
                        key={cidx}
                        sx={{
                          wordBreak: "break-word",
                        }}
                      >
                        <Markdown>{String(cell)}</Markdown>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      );
    }

    case "callout": {
      const callout = block as ApiCalloutBlock;
      return (
        <Alert
          severity={callout.variant}
          variant="outlined"
          sx={{ mt: chatTheme.spacing?.componentMargin ?? 1 }}
        >
          {callout.title && (
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              {callout.title}
            </Typography>
          )}
          <Typography variant="body2">{callout.content}</Typography>
        </Alert>
      );
    }

    case "insight": {
      const insight = block as ApiInsightBlock;
      return (
        <Card
          variant="outlined"
          sx={{
            mt: chatTheme.spacing?.componentMargin ?? 2,
            borderLeft: 3,
            borderLeftColor: "info.main",
            bgcolor: "info.light",
            backgroundColor: (theme) => theme.palette.info.light + "22",
          }}
        >
          <CardContent>
            <Typography variant="overline" color="info.main">
              Insight {insight.confidence && `(${insight.confidence} confidence)`}
            </Typography>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {insight.title}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {insight.summary}
            </Typography>
            {insight.details?.map((detail, idx) => (
              <Typography key={idx} variant="body2">
                • {detail}
              </Typography>
            ))}
            {insight.sources && insight.sources.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="textSecondary">
                  {insight.sources.length === 1 ? "Source:" : "Sources:"}
                </Typography>
                {insight.sources.map((source, idx) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: "block", ml: 1 }}
                  >
                    •{" "}
                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                      {source.title}
                    </a>
                    {source.attribution && ` (${source.attribution})`}
                  </Typography>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      );
    }

    case "recommendation": {
      const recommendation = block as ApiRecommendationBlock;
      return (
        <Card
          variant="outlined"
          sx={{
            mt: chatTheme.spacing?.componentMargin ?? 2,
            borderLeft: 3,
            borderLeftColor: "warning.main",
            bgcolor: "warning.light",
            backgroundColor: (theme) => theme.palette.warning.light + "22",
          }}
        >
          <CardContent>
            <Typography variant="overline" color="warning.main">
              Recommendation ({recommendation.priority} priority)
            </Typography>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {recommendation.title}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {recommendation.description}
            </Typography>
            {recommendation.impact && (
              <Typography variant="body2" color="textSecondary">
                <strong>Impact:</strong> {recommendation.impact}
              </Typography>
            )}
            {recommendation.effort && (
              <Typography variant="body2" color="textSecondary">
                <strong>Effort:</strong> {recommendation.effort}
              </Typography>
            )}
            {recommendation.timeline && (
              <Typography variant="body2" color="textSecondary">
                <strong>Timeline:</strong> {recommendation.timeline}
              </Typography>
            )}
          </CardContent>
        </Card>
      );
    }

    case "divider": {
      return <Divider />;
    }

    default:
      return null;
  }
};
